// A-10 — Onglet Timeline.
// V1 : flux des transitions d'étape (dossier_stages).
// V2 (EPIC-K) : ajout des évènements signature_* depuis dossier_activity_log.
import { format, formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileCheck2,
  FolderPlus,
  GitCommit,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { supabase } from '../../lib/supabaseClient';
import { Spinner } from '../ui/Spinner';
import { useDossierStages } from '../../hooks/queries/useDossiers';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { CollaboratorLink } from '../collaborators/CollaboratorLink';
import {
  DOSSIER_STAGE_LABELS,
  type Dossier,
  type DossierStage,
} from '../../services/dossiers/dossierService';

// ─── Signature event config ───────────────────────────────────────────────────

type SignatureAction =
  | 'signature_circuit_created'
  | 'signature_step_signed'
  | 'signature_step_rejected'
  | 'signature_circuit_cancelled'
  | 'signature_circuit_finalized';

interface SignatureEventConfig {
  label: string;
  icon: React.ReactNode;
}

const SIGNATURE_ACTION_CONFIG: Record<SignatureAction, SignatureEventConfig> = {
  signature_circuit_created: {
    label: 'Circuit de signature lancé',
    icon: <ClipboardCheck className="w-4 h-4 text-blue-600" />,
  },
  signature_step_signed: {
    label: 'Étape signée',
    icon: <CheckCircle2 className="w-4 h-4 text-emerald-600" />,
  },
  signature_step_rejected: {
    label: 'Étape refusée',
    icon: <XCircle className="w-4 h-4 text-red-500" />,
  },
  signature_circuit_cancelled: {
    label: 'Circuit annulé',
    icon: <MinusCircle className="w-4 h-4 text-neutral-500" />,
  },
  signature_circuit_finalized: {
    label: 'Document tamponné',
    icon: <FileCheck2 className="w-4 h-4 text-emerald-700" />,
  },
};

function signatureSubline(action: SignatureAction, details: Record<string, unknown>): string | undefined {
  if (action === 'signature_circuit_created' && details?.title) {
    const parts: string[] = [`« ${details.title} »`];
    if (details.nb_steps) parts.push(`${details.nb_steps} signataire(s)`);
    return parts.join(' — ');
  }
  if (action === 'signature_step_rejected' && details?.comment) {
    return `Motif : ${details.comment}`;
  }
  return undefined;
}

// ─── Activity log query ───────────────────────────────────────────────────────

interface ActivityLogEntry {
  id: string;
  action: string;
  actor_id: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

const SIGNATURE_ACTIONS: SignatureAction[] = [
  'signature_circuit_created',
  'signature_step_signed',
  'signature_step_rejected',
  'signature_circuit_cancelled',
  'signature_circuit_finalized',
];

async function listDossierSignatureEvents(dossierId: string): Promise<ActivityLogEntry[]> {
  const { data, error } = await supabase
    .from('dossier_activity_log')
    .select('id, action, actor_id, details, created_at')
    .eq('dossier_id', dossierId)
    .in('action', SIGNATURE_ACTIONS)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as ActivityLogEntry[];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DossierTimelineTab({ dossier }: { dossier: Dossier }) {
  const { data: stages, isLoading: stagesLoading } = useDossierStages(dossier.id);
  const { data: sigEvents, isLoading: sigLoading } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dossier-signature-events', dossier.id],
    queryFn: () => listDossierSignatureEvents(dossier.id),
  });
  const { data: collaborators } = useCollaborators();

  if (stagesLoading || sigLoading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>;
  }

  const collabName = (id: string | null) => {
    if (!id) return 'Système';
    const c = collaborators?.find((x) => x.id === id);
    return c?.full_name || c?.email || 'Inconnu';
  };

  // Merge stage transitions + signature events, sorted by date ascending
  type MergedItem =
    | { kind: 'stage'; date: string; id: string; data: NonNullable<typeof stages>[number] }
    | { kind: 'sig'; date: string; id: string; data: ActivityLogEntry };

  const merged: MergedItem[] = [
    ...(stages ?? []).map((s) => ({ kind: 'stage' as const, date: s.transitioned_at, id: s.id, data: s })),
    ...(sigEvents ?? []).map((e) => ({ kind: 'sig' as const, date: e.created_at, id: e.id, data: e })),
  ].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h2 className="text-lg font-semibold text-cime-text-primary mb-4">
        Historique
      </h2>

      <ol className="relative border-l border-neutral-200 ml-3 space-y-5 pl-6">
        {/* Création (pseudo-événement) */}
        <TimelineItem
          when={dossier.opened_at}
          icon={<FolderPlus className="w-4 h-4 text-cime-primary" />}
          title={
            <>
              <strong>{dossier.created_by ? <CollaboratorLink collaboratorId={dossier.created_by} title={`Voir le profil de ${collabName(dossier.created_by)}`}>{collabName(dossier.created_by)}</CollaboratorLink> : collabName(dossier.created_by)}</strong> a ouvert le dossier
            </>
          }
          subtitle={`Étape initiale : ${DOSSIER_STAGE_LABELS[dossier.stage]}`}
        />

        {merged.map((item) => {
          if (item.kind === 'stage') {
            const s = item.data;
            return (
              <TimelineItem
                key={item.id}
                when={s.transitioned_at}
                icon={<GitCommit className="w-4 h-4 text-blue-600" />}
                title={
                  <>
                    <strong>{s.transitioned_by ? <CollaboratorLink collaboratorId={s.transitioned_by} title={`Voir le profil de ${collabName(s.transitioned_by)}`}>{collabName(s.transitioned_by)}</CollaboratorLink> : collabName(s.transitioned_by)}</strong> a fait passer le dossier
                    {' '}<StageTransition
                      from={s.from_stage}
                      to={s.to_stage}
                      fromLabel={(s as { from_label?: string | null }).from_label}
                      toLabel={(s as { to_label?: string | null }).to_label}
                    />
                  </>
                }
                subtitle={s.note ?? undefined}
              />
            );
          }

          // Signature event
          const e = item.data;
          const action = e.action as SignatureAction;
          const cfg = SIGNATURE_ACTION_CONFIG[action];
          if (!cfg) return null;
          const sub = signatureSubline(action, e.details);
          return (
            <TimelineItem
              key={item.id}
              when={e.created_at}
              icon={cfg.icon}
              title={
                <>
                  <strong>{e.actor_id ? <CollaboratorLink collaboratorId={e.actor_id} title={`Voir le profil de ${collabName(e.actor_id)}`}>{collabName(e.actor_id)}</CollaboratorLink> : collabName(e.actor_id)}</strong>{' '}— {cfg.label}
                </>
              }
              subtitle={sub}
            />
          );
        })}

        {merged.length === 0 && (
          <li className="text-sm italic text-cime-text-muted pl-2">
            Aucun événement pour le moment.
          </li>
        )}
      </ol>
    </div>
  );
}

function TimelineItem({
  when, icon, title, subtitle,
}: {
  when: string;
  icon: React.ReactNode;
  title: React.ReactNode;
  subtitle?: string;
}) {
  return (
    <li className="relative">
      <span className="absolute -left-[34px] top-0 w-6 h-6 rounded-full bg-white border border-neutral-200 flex items-center justify-center">
        {icon}
      </span>
      <div className="bg-white border border-neutral-200 rounded-md px-3 py-2 text-sm">
        <p className="text-cime-text-primary">{title}</p>
        {subtitle && (
          <p className="mt-0.5 text-cime-text-muted text-xs">{subtitle}</p>
        )}
        <p className="mt-1 text-[10px] text-neutral-400 tabular-nums">
          {format(new Date(when), "d MMM yyyy 'à' HH:mm", { locale: fr })}
          {' · '}
          {formatDistanceToNow(new Date(when), { locale: fr, addSuffix: true })}
        </p>
      </div>
    </li>
  );
}

function StageTransition({
  from,
  to,
  fromLabel,
  toLabel,
}: {
  from: DossierStage | null;
  to: DossierStage;
  fromLabel?: string | null;
  toLabel?: string | null;
}) {
  const fromText = fromLabel || (from ? DOSSIER_STAGE_LABELS[from] : null);
  const toText = toLabel || DOSSIER_STAGE_LABELS[to];
  return (
    <span className="inline-flex items-center gap-1 align-middle text-cime-text-primary">
      {fromText && (
        <>
          <span className="px-1.5 py-0.5 text-[11px] rounded bg-neutral-100 text-neutral-700 border border-neutral-200">
            {fromText}
          </span>
          <ArrowRight className="w-3 h-3 text-neutral-400" />
        </>
      )}
      <span className="px-1.5 py-0.5 text-[11px] rounded bg-blue-50 text-blue-700 border border-blue-200">
        {toText}
      </span>
    </span>
  );
}
